import React, { useState } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { CheckIcon, ClockIcon, Flame } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { motion, AnimatePresence } from 'framer-motion';
const baseFeatures = ['11 Tomos completos', 'Bono #1: Láminas Pro', 'Bono #2: Cuaderno del Asador', 'Bono #3: Tablas Secretas del Campo', 'Bono #4: Pack Anti-Vergüenza', 'Acceso inmediato en PDF', 'Actualizaciones de por vida'];
const orderBumpTitles = [{
  title: 'Mollejas de Corazón',
  hint: 'Crocantes por fuera, cremosas por dentro'
}, {
  title: 'Chinchulín de Cristal',
  hint: 'Dorado y crocante, sin gusto fuerte'
}, {
  title: 'Costillar Legendario',
  hint: 'Que se desarme solo, técnica de 4hs'
}, {
  title: 'Entraña Pro',
  hint: 'Punto exacto en 15 min, sin pasarse'
}, {
  title: 'Vacío Legendario',
  hint: 'La costra perfecta y el punto rosado'
}];
type PricingOption = 'base' | 'premium';
export function PricingSection() {
  const timeLeft = useCountdown(14, 16);
  const [selected, setSelected] = useState<PricingOption>('premium');
  return <section id="compra" className="bg-zinc-950 relative scroll-mt-10" data-id="element-131">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[600px] bg-red-900/10 blur-[120px] rounded-full pointer-events-none" data-id="element-132" />

      <div className="container mx-auto px-4 relative z-10" data-id="element-133">
        <div className="max-w-3xl mx-auto" data-id="element-134">
          <div className="min-h-[100dvh] flex flex-col justify-center py-4 md:py-24" data-id="element-135">
            {/* Header */}
            <div className="text-center mb-3 md:mb-8" data-id="element-136">
              <div className="inline-flex items-center justify-center gap-1.5 bg-red-950/50 border border-red-900/50 text-red-500 px-3 py-1 rounded-full font-mono text-sm md:text-xl font-bold mb-3 md:mb-6 shadow-[0_0_15px_rgba(220,38,38,0.2)]" data-id="element-137">
                <ClockIcon className="w-3.5 h-3.5 md:w-5 md:h-5 animate-pulse" data-id="element-138" />
                <span data-id="element-139">EXPIRA EN: {timeLeft}</span>
              </div>
              <h2 className="text-xl md:text-4xl font-serif font-bold text-white mb-0.5 md:mb-2" data-id="element-140">
                Elegí tu <span className="text-amber-500" data-id="element-141">PACK:</span>
              </h2>
              <p className="text-zinc-400 text-xs md:text-base" data-id="element-142">
                Todas las opciones incluyen los 4 bonos estratégicos
              </p>
            </div>

            {/* Option Cards */}
            <div className="space-y-2.5 md:space-y-4 mb-4 md:mb-8" data-id="element-143">
              {/* Option 1: Base */}
              <button onClick={() => setSelected('base')} className={`w-full text-left rounded-xl md:rounded-2xl transition-all duration-300 border-2 overflow-hidden ${selected === 'base' ? 'bg-zinc-900 border-amber-500 shadow-[0_0_30px_rgba(245,158,11,0.15)]' : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-700'}`} data-id="element-144">
                <div className="flex items-center gap-3 md:gap-4 p-3.5 md:p-6" data-id="element-145">
                  <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selected === 'base' ? 'border-amber-500' : 'border-zinc-600'}`} data-id="element-146">
                    {selected === 'base' && <motion.div layoutId="radioIndicator" className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-amber-500" transition={{
                    type: 'spring',
                    bounce: 0.3,
                    duration: 0.4
                  }} data-id="element-147" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-148">
                    <div className="flex items-center gap-1.5" data-id="element-149">
                      <span className="text-sm md:text-lg" data-id="element-150">🔥</span>
                      <h3 className="font-bold text-white text-xs md:text-lg uppercase tracking-wide" data-id="element-151">
                        Mega Pack Maestro Asador
                      </h3>
                    </div>
                    <p className="text-zinc-400 text-[11px] md:text-sm" data-id="element-152">
                      11 Tomos + 4 Bonos + Acceso de por vida
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0" data-id="element-153">
                    <p className="text-zinc-500 text-[10px] md:text-sm line-through" data-id="element-154">
                      $85.950
                    </p>
                    <p className={`font-bold text-lg md:text-3xl ${selected === 'base' ? 'text-amber-400' : 'text-white'}`} data-id="element-155">
                      $19.990
                    </p>
                  </div>
                </div>

                {/* Expanded info for base */}
                <AnimatePresence data-id="element-156">
                  {selected === 'base' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-157">
                      <div className="px-3.5 pb-3.5 md:px-6 md:pb-6 pt-0" data-id="element-158">
                        <div className="border-t border-zinc-800 pt-3 md:pt-4" data-id="element-159">
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 md:gap-y-2" data-id="element-160">
                            {baseFeatures.map((item, i) => <div key={i} className="flex items-center gap-1.5 text-zinc-300 text-[11px] md:text-sm" data-id="element-161">
                                <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" data-id="element-162" />
                                <span data-id="element-163">{item}</span>
                              </div>)}
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </button>

              {/* Option 2: Premium */}
              <button onClick={() => setSelected('premium')} className={`w-full text-left rounded-xl md:rounded-2xl transition-all duration-300 border-2 overflow-hidden ${selected === 'premium' ? 'bg-zinc-900 border-amber-500 shadow-[0_0_30px_rgba(245,158,11,0.15)]' : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-700'}`} data-id="element-164">
                <div className="flex items-center gap-3 md:gap-4 p-3.5 md:p-6" data-id="element-165">
                  <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selected === 'premium' ? 'border-amber-500' : 'border-zinc-600'}`} data-id="element-166">
                    {selected === 'premium' && <motion.div layoutId="radioIndicator" className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-amber-500" transition={{
                    type: 'spring',
                    bounce: 0.3,
                    duration: 0.4
                  }} data-id="element-167" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-168">
                    <div className="flex items-center gap-1.5 flex-wrap" data-id="element-169">
                      <span className="text-sm md:text-lg" data-id="element-170">🏆</span>
                      <h3 className="font-bold text-white text-xs md:text-lg uppercase tracking-wide" data-id="element-171">
                        Pack Completo + Extras
                      </h3>
                    </div>
                    <span className="inline-flex items-center gap-1 bg-amber-500/20 text-amber-400 text-[9px] md:text-[11px] font-bold px-1.5 py-px md:px-2 md:py-0.5 rounded-full uppercase tracking-wider" data-id="element-172">
                      ⭐ Más elegido
                    </span>
                    <p className="text-zinc-400 text-[11px] md:text-sm mt-0.5" data-id="element-173">
                      11 Tomos + 4 Bonos + 5 Extras
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0" data-id="element-174">
                    <p className="text-zinc-500 text-[10px] md:text-sm line-through" data-id="element-175">
                      $135.900
                    </p>
                    <p className={`font-bold text-lg md:text-3xl ${selected === 'premium' ? 'text-amber-400' : 'text-white'}`} data-id="element-176">
                      $29.990
                    </p>
                  </div>
                </div>

                {/* Expanded info for premium */}
                <AnimatePresence data-id="element-177">
                  {selected === 'premium' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-178">
                      <div className="px-3.5 pb-3.5 md:px-6 md:pb-6 pt-0" data-id="element-179">
                        <div className="border-t border-zinc-800 pt-3 md:pt-4" data-id="element-180">
                          {/* Base features compact */}
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 md:gap-y-2 mb-3 md:mb-4" data-id="element-181">
                            {baseFeatures.map((item, i) => <div key={i} className="flex items-center gap-1.5 text-zinc-300 text-[11px] md:text-sm" data-id="element-182">
                                <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" data-id="element-183" />
                                <span data-id="element-184">{item}</span>
                              </div>)}
                          </div>
                          {/* Order bumps compact */}
                          <div className="border-t border-zinc-800 pt-2.5 md:pt-4" data-id="element-185">
                            <p className="text-[10px] md:text-xs text-zinc-500 font-bold uppercase tracking-wider mb-2 md:mb-3 flex items-center gap-1" data-id="element-186">
                              <Flame className="w-3 h-3 md:w-4 md:h-4 text-amber-500" data-id="element-187" />
                              + 5 Extras Exclusivos:
                            </p>
                            <div className="space-y-1.5 md:space-y-2" data-id="element-188">
                              {orderBumpTitles.map((bump, i) => <div key={i} className="flex items-start gap-1.5 text-[11px] md:text-sm" data-id="element-189">
                                  <Flame className="w-3 h-3 text-amber-500/70 flex-shrink-0 mt-0.5" data-id="element-190" />
                                  <div data-id="element-191">
                                    <span className="text-amber-400 font-semibold" data-id="element-192">
                                      {bump.title}
                                    </span>
                                    <span className="text-zinc-500 ml-1" data-id="element-193">
                                      — {bump.hint}
                                    </span>
                                  </div>
                                </div>)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </button>
            </div>

            {/* CTA Button */}
            <div className="text-center" data-id="element-194">
              <CTAButton href={selected === 'premium' ? 'https://asador.ebookditales.com/cart/45034549575759:1' : 'https://asador.ebookditales.com/cart/45034549182543:1'} text={selected === 'premium' ? '👉 QUIERO TODO: SISTEMA + EXTRAS →' : '👉 SÍ, QUIERO EL SISTEMA COMPLETO →'} className="w-full" data-id="element-195" />
              <p className="mt-2 md:mt-4 text-zinc-500 text-[11px] md:text-sm italic" data-id="element-196">
                {selected === 'premium' ? 'Incluye todo. No necesitás agregar nada más.' : 'Podés sumar los extras en el checkout'}
              </p>
              <div className="mt-1.5 md:mt-3 flex items-center justify-center gap-1.5 text-[10px] md:text-xs text-zinc-500" data-id="element-197">
                <span className="inline-block w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-green-500 animate-pulse" data-id="element-198" />
                Pago 100% seguro · Acceso inmediato · Descarga en PDF
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}